import { event, sitecore } from '../../Foundation';
import moment from 'moment';

export class DataTable {
  constructor(element) {
    element.DataTable = this;

    this._refs = {
      el: element,
      header: element.querySelector('.datatable__header'),
      body: element.querySelector('.datatable__body'),
      loading: element.querySelector('.datatable__loading'),
    };

    // Find items based on refs
    //this._refs.items = Array.from(this._refs.body.querySelectorAll('.datatable__item'))

    this._refs.items = Array.from(this._refs.body.children);

    this._data = Object.assign({}, this._settings, this._dataset);
    if (this._refs.items.length) {
      this._data.items = this._refs.items;
    }

    // Create, attach and render items eg.
    // # Footer
    this._footer();

    // # Header
    if (this._refs.header != null) {
      this._header();
    }

    // // # Body
    this._body();

    // # hide loading screen
    if (this._refs.loading != null) {
      this._refs.loading.classList.add('hide');
    }
  }

  _header() {
    // Find sortable header
    const arrSortableFields = Array.from(this._refs.header.querySelectorAll('.is-sortable')),
      x = arrSortableFields.filter(
        (x) => x.classList.contains('is-desc') || x.classList.contains('is-asc'),
      );

    // If no default-sorting has been set, default to use first column
    if (!x.length) {
      this._data.sort.current.field = arrSortableFields[0];
      this._data.sort._default.field = arrSortableFields[0];

      // Execute a sort
      this._onSortableItemClick();
    } else {
      const intPosition = arrSortableFields.indexOf(x[0]);
      this._data.sort.current = {
        field: arrSortableFields[intPosition],
        order: arrSortableFields[intPosition].classList.contains('is-desc') ? 'desc' : 'asc',
      };
      this._data.sort._default = {
        field: arrSortableFields[intPosition],
        order: arrSortableFields[intPosition].classList.contains('is-desc') ? 'desc' : 'asc',
      };
    }

    // If default sorting has been set
    arrSortableFields.forEach((x) =>
      x.addEventListener('click', this._onSortableItemClick.bind(this)),
    );

    // TODO: Due to an issue with one of the spots currently using the component, we need to disable the filters while in sitecore context
    // due to use of EditFrame
    if (!sitecore.isInPageEditor) {
      // Boot filters
      this._appendFilters();

      // Append note
      if (
        typeof this._data.translations.note != 'undefined' &&
        this._data.translations.note.trim().length
      ) {
        const oNote = this._createElement('div', {
          class: 'datatable__note',
          innerHTML: `<span class="label--required">*</span> ${this._data.translations.note}`,
        });

        this._refs.el.insertBefore(oNote, this._refs.header);
      }
    }
  }

  /**
   * Pre-processing of items eg. for rendering of related content within body-contentarea
   */
  async _body() {
    await this._togglItems();
  }

  _footer() {
    const oFooter = this._createElement('div', { class: 'datatable__footer' }),
      oRow = this._createElement('div', { class: 'row' }, oFooter),
      oInfo = this._createElement('div', { class: 'col-sm-12 col-md-3' }, oRow),
      oPagination = this._createElement('div', { class: 'col-sm-12 col-md-6 text-center' }, oRow),
      oPagesize = this._createElement('div', { class: 'col-sm-12 col-md-3' }, oRow);

    // Asign elements to global scope
    this._refs.footer = oFooter;
    this._refs.pagesize = oPagesize;
    this._refs.pagination = oPagination;
    this._refs.info = oInfo;

    // Create sub-elements
    this._appendPagesize();
    this._appendInfo();

    const rhytm = this._paginate(
      this._refs.items.length,
      this._data.currentPage,
      this._data.pageSize.current,
      this._data.maxPages,
    );
    this._appendPagination(rhytm.pages, rhytm.currentPage, rhytm.startPage, rhytm.endPage);

    this._refs.el.insertBefore(oFooter, this._refs.body.nextSibling);
  }

  /**
   * Create and append filters/facets for enhancing the user experience
   */
  _appendFilters() {
    const arrFilterableItems = Array.from(this._refs.header.querySelectorAll('.is-filterable'));
    if (!arrFilterableItems.length) {
      return;
    }

    // Create base
    const oHtml = this._createElement('div', { class: 'datatable__filters' }),
      oRow = this._createElement('div', { class: 'row' }, oHtml);

    // Create & configure filterable items
    for (const x of arrFilterableItems) {
      const fieldname = this._createGUID(),
        intPosition = Array.from(x.parentNode.children).indexOf(x);

      // Add filter name to global storage + add to columns for future reference
      x.setAttribute('data-id', fieldname);
      x.setAttribute('data-column', intPosition);

      if (x.dataset.filtertype === 'date') {
        let arrDates = [];
        this._data.items.forEach((y) => {
          let oCell = Array.from(y.querySelectorAll('.datatable__cell'))[intPosition];
          arrDates.push(moment(this._fetchText(oCell)).format('YYYY-MM-DD'));
        });

        const arrSortedDays = arrDates.sort((a, b) => a - b),
          /* eslint-disable-next-line */
          fieldGenerator = (fieldtype, args) => {
            const oFieldOptions = {
                preventDefault: true,
                minDate: arrSortedDays[0],
                maxDate: arrSortedDays[arrDates.length - 1],
                format: this._data.dateFormat,
              },
              oColumn = this._createElement('div', { class: 'col-sm-12 col-md-2' }, oRow),
              oInputgroup = this._createElement('div', { class: 'input-group' }, oColumn),
              oInput = this._createElement(
                'input',
                {
                  type: 'text',
                  'data-column': intPosition,
                  class: 'form-control is-' + fieldtype,
                  'data-datepicker': JSON.stringify(oFieldOptions),
                },
                oInputgroup,
              );

            oInput.addEventListener('change', this._onFilterChange.bind(this));

            this._createElement(
              'i',
              {
                class: 'eva eva-calendar-outline',
              },
              oInputgroup,
            );
          };

        // Create elements
        fieldGenerator('start');
        fieldGenerator('end');
      } else if (typeof x.dataset.filtertype == 'undefined') {
        // Dropdown filter
        const oColumn = this._createElement(
          'div',
          {
            class: 'col-sm-12 col-md-3',
          },
          oRow,
        );
        // Get corresponding data from current position within items
        let arrData = [];
        this._refs.items.forEach((row) => {
          const oCells = Array.from(row.querySelectorAll('.datatable__cell'));

          if (oCells[intPosition]) {
            arrData.push(this._fetchText(oCells[intPosition]));
          }
        });

        const arrDistinctValues = new Set(
            arrData.sort((a, b) =>
              !!window.MSInputMethodContext && !!document.documentMode
                ? a.localeCompare(b)
                : a.localeCompare(b, undefined, this._data.sort.options),
            ),
          ),
          oFilter = this._createElement(
            'div',
            {
              class: 'form-select',
              'data-select': '',
            },
            oColumn,
          ),
          oFilterDropdown = this._createElement(
            'select',
            {
              name: fieldname,
              'data-column': intPosition,
              innerHTML: `<option data-count="${this._refs.items.length}">${this._fetchText(
                x,
              )}</option>${Array.from(arrDistinctValues)
                .map(
                  (item) =>
                    `<option value="${item}" data-count="${this._countItems(
                      intPosition,
                      item,
                    )}">${item}</option>`,
                )
                .join('')}`,
            },
            oFilter,
          );

        if (this._refs.el.getAttribute('data-filter-mode') === 'inc-count') {
          oFilter.setAttribute('data-mode', 'inc-count');
        }

        oFilterDropdown.addEventListener('change', this._onFilterChange.bind(this));
      }
    }

    // Create Clear filters
    this._refs.clearFilter = this._createElement(
      'a',
      {
        onclick: this._onClearFilterClick.bind(this),
        class: 'datatable__clear-filters is-hidden',
        innerHTML: `<i class="eva eva-close-circle-outline"></i> ${
          typeof this._data.translations.clearFilters != 'undefined' &&
          this._data.translations.clearFilters.trim().length
            ? this._data.translations.clearFilters
            : 'Clear filters'
        }`,
      },
      oHtml,
    );

    this._refs.el.insertBefore(oHtml, this._refs.header);
  }

  /**
   * Count items for filter
   **/
  _countItems(pos, filter) {
    return this._refs.items.filter((item) => {
      const cellText = this._fetchText(Array.from(item.querySelectorAll('.datatable__cell'))[pos]);
      return filter.toLowerCase().trim() === cellText.toLowerCase().trim();
    }).length;
  }

  _countTotalItems() {
    return this._refs.items.length;
  }

  /**
   * Append/Update current table information
   */
  _appendInfo() {
    const rhytm = this._paginate(
        this._refs.items.length,
        this._data.currentPage,
        this._data.pageSize.current,
        this._data.maxPages,
      ),
      text =
        typeof this._data.translations.info != 'undefined' &&
        this._data.translations.info.trim().length
          ? this._data.translations.info
          : 'Showing {startIndex} to {endIndex} of {total} results',
      oHtml = this._createElement('span', {
        class: 'datatable__info',
        innerHTML: text
          .replace('{startIndex}', parseInt(rhytm.startIndex + 1))
          .replace('{endIndex}', parseInt(rhytm.endIndex + 1))
          .replace('{total}', rhytm.totalItems),
      });

    this._refs.info.innerHTML = '';
    this._refs.info.appendChild(oHtml);
  }

  /**
   * Create and append PageSize dropdown
   */
  _appendPagesize() {
    const oHtml = this._createElement('div', {
        class: 'form-select',
        'data-select': '',
      }),
      oSelect = this._createElement(
        'select',
        {
          name: 'pagesize',
          innerHTML: `${this._settings.pageSize.values
            .map((item) => `<option value="${item}">${item}</option>`)
            .join('')}`,
        },
        oHtml,
      );

    // Event binding
    oSelect.addEventListener('change', this._onPagesizeChange.bind(this));

    // Append HTML
    this._refs.pagesize.appendChild(oHtml);
  }

  /**
   * Append pagination to component
   */
  _appendPagination(pages, currentPage, startPage, endPage) {
    let oHtml = this._createElement('ul', {
      class: 'v-pagination',
    });

    // Create left-arrow
    const oArrowLeft = this._createElement(
        'li',
        {
          class: currentPage === startPage ? 'disabled' : '',
        },
        oHtml,
      ),
      /* eslint-disable-next-line */
      oArrowLeftLink = this._createElement(
        'a',
        {
          href: '#',
          class: 'is-left',
          innerHTML: '&lsaquo;',
          onclick: this._onPaginationArrowClick.bind(this),
        },
        oArrowLeft,
      );

    // Items
    pages.forEach((x) => {
      let oItem = this._createElement(
          'li',
          {
            class: currentPage === x ? 'active' : '',
          },
          oHtml,
        ),
        /* eslint-disable-next-line */
        oLink = this._createElement(
          'a',
          {
            href: '#',
            innerHTML: x,
            onclick: this._onPaginationItemClick.bind(this),
          },
          oItem,
        );
    });

    // right arrow
    const oArrowRight = this._createElement(
        'li',
        {
          class: currentPage == endPage ? 'disabled' : '',
        },
        oHtml,
      ),
      /* eslint-disable-next-line */
      oArrowRightLink = this._createElement(
        'a',
        {
          href: '#',
          class: 'is-right',
          innerHTML: '&rsaquo;',
          onclick: this._onPaginationArrowClick.bind(this),
        },
        oArrowRight,
      );

    this._refs.pagination.innerHTML = '';
    this._refs.pagination.appendChild(oHtml);
  }

  // # Events

  /**
   * When user selects a different pagesize
   * @param {HTMLEvent} e - Event interface
   */
  _onPagesizeChange(e) {
    this._data.currentPage = 1;
    this._data.pageSize.current = e.target.options[e.target.selectedIndex].value;
    this._togglItems();
  }

  /**
   * When a user clicks one of the pagination arrows
   * @param {HtmlElement} e - Event interface
   */
  _onPaginationArrowClick(e) {
    e.preventDefault();

    const link = e.target;

    // Check if parent element is disabled in order to prevent
    if (link.parentElement.classList.contains('disabled')) {
      return false;
    }

    // Direction
    if (e.target.classList.contains('is-left')) {
      this._data.currentPage--;
    } else {
      this._data.currentPage++;
    }

    this._togglItems();
    event.emit(document, 'datatable.paginate');
  }

  /**
   * When user clicks on a link in pagination container
   * @param {Event} e - Event interface
   */
  _onPaginationItemClick(e) {
    e.preventDefault();

    this._data.currentPage = parseInt(e.target.innerHTML);

    this._togglItems();
    event.emit(document, 'datatable.paginate');
  }

  /**
   * Handling of click on table headers to start sorting (asc/desc)
   * @param {Event} e - Event interface
   */
  _onSortableItemClick(e) {
    const item = (this._data.sort.current.field =
        e == null ? this._data.sort.current.field : e.target),
      fields = Array.from(this._refs.header.children);

    // Update sort order
    this._data.sort.current.order = item.classList.contains('is-asc') ? 'desc' : 'asc';

    // Icons (Ascending/Descending)
    fields.forEach((x) => {
      x.classList.remove('is-desc');
      x.classList.remove('is-asc');
    });

    // Set UI State
    item.classList.add(this._data.order[this._data.sort.current.order]);

    // append
    this._refs.items = this._refs.items.sort(this._sort.bind(this));
    this._refs.items.forEach((x) => this._refs.body.appendChild(x));

    // Reset currentPage
    this._data.currentPage = 1;
    this._togglItems();
  }

  /**
   * Reset filters and item rendering to default
   * @param {Event} e - Event interface
   */
  _onClearFilterClick(e) {
    e.preventDefault();

    // Reset drodowns to intial state
    this._data.facets.forEach((oFacet) => {
      const fieldtype = oFacet.field.tagName.toLowerCase();
      switch (fieldtype) {
        case 'input':
          oFacet.field.value = '';
          break;
        case 'select':
          Array.from(oFacet.field.options).forEach((opt, ind) => {
            if (ind === 0) {
              opt.selected = true;
            }
            opt.disabled = false;
            opt.selected = false;
          });

          event.emit(oFacet.field, 'reflow');
          event.emit(oFacet.field, 'refresh');
          break;
      }
    });

    // Reset facets & sorting
    this._data.facets = [];
    this._data.sort.current = this._data.sort._default;

    const fields = Array.from(this._refs.header.children);
    // Icons (Ascending/Descending)
    fields.forEach((x) => {
      x.classList.remove('is-desc');
      x.classList.remove('is-asc');
    });

    // Set UI State
    this._data.sort.current.field.classList.add(this._data.order[this._data.sort.current.order]);

    // Re-iterate items
    this._onFilterChange(e);
  }

  /**
   * Handler for re-attaching items based on filters
   * // # TODO: Find a way to fetch current dateformat from Sitecore, right now hardcoded to US
   */
  _onFilterChange(e) {
    // Find all facets // TODO: OPTIMIZE
    let arrFacets = [],
      arrInputs = this._getFilters();

    this._refs.prevTotalItems = this._refs.items.length;
    for (const x of arrInputs) {
      if (x.tagName.toLowerCase() === 'select') {
        const result = Array.from(x.options).filter((y, i) => y.selected && i > 0);
        if (result.length) {
          arrFacets.push({ field: x, val: this._fetchText(result[0]).toLowerCase() });
        }
      } else {
        if (x.value.trim().length) {
          arrFacets.push({ field: x, val: x.value.toLowerCase().trim() });
        }
      }
    }

    this._refs.items.forEach((y) => this._refs.body.removeChild(y));

    // Initiating sort-sequence
    this._refs.items = this._data.items.filter((item) => {
      let arrCells = Array.from(item.querySelectorAll('.datatable__cell')),
        matches = 0;

      for (const facet of arrFacets) {
        const cellValue =
          arrCells[facet.field.dataset.column].dataset.sort ||
          this._fetchText(arrCells[facet.field.dataset.column]).toLowerCase();

        if (moment(cellValue, this._data.dateFormat, true).isValid()) {
          if (
            facet.field.classList.contains('is-start') &&
            new Date(cellValue) >= new Date(facet.val)
          ) {
            matches++;
          } else if (
            facet.field.classList.contains('is-end') &&
            new Date(cellValue) <= new Date(facet.val)
          ) {
            matches++;
          }
        } else if (facet.val === cellValue) {
          matches++;
        }
      }

      return matches == arrFacets.length;
    });

    // Error handling
    if (!this._refs.items.length) {
      // No items were found
      this._refs.body.innerHTML = `<div class="alert alert-info">${
        typeof this._data.translations.noResults != 'undefined' &&
        this._data.translations.noResults.trim().length
          ? this._data.translations.noResults
          : 'No results were found with current filters, please try with other values.'
      }</div>`;
      this._refs.footer.classList.add('is-hidden');
      this._refs.header.classList.add('is-hidden');
    } else {
      this._refs.body.innerHTML = ``;
      this._refs.footer.classList.remove('is-hidden');
      this._refs.header.classList.remove('is-hidden');

      // Sort and append
      this._refs.items.sort(this._sort.bind(this));
      this._refs.items.forEach((x) => this._refs.body.appendChild(x));
    }

    // Update dependencies
    this._data.facets = arrFacets;
    this._data.currentPage = 1;
    this._togglItems();

    // Update depdencies
    if (this._refs.clearFilter != null) {
      if (arrFacets.length) {
        this._refs.clearFilter.classList.remove('is-hidden');
      } else {
        this._refs.clearFilter.classList.add('is-hidden');
      }
    }

    this._onUpdateFilterValues(e);
  }

  /**
   * Execute filter-updates once filtering has completed
   */
  _fetchText(string) {
    return string.innerText.trim() || string.textContent.trim();
  }
  /* eslint-disable-next-line */
  _onUpdateFilterValues(e) {
    let arrFilters = this._getFilters();

    for (const x of arrFilters) {
      switch (
        x.tagName.toLowerCase() // Operators
      ) {
        case 'select':
          /* eslint-disable-next-line */
          const oHeaderCell = Array.from(this._refs.header.children).filter(
              (y) => x.name === y.getAttribute('data-id'),
            ),
            intPosition = Array.from(this._refs.header.children).indexOf(oHeaderCell[0]),
            arrData = [];

          // Generate data
          this._refs.items.filter((item) => {
            const oCells = Array.from(item.querySelectorAll('.datatable__cell'));
            if (oCells[intPosition]) {
              arrData.push(this._fetchText(oCells[intPosition]));
            }
          });

          // Reset dropdown

          for (const [index, option] of Array.from(x.options).entries()) {
            if (index > 0 && ![...new Set(arrData)].includes(this._fetchText(option))) {
              option.disabled = true;
              option.setAttribute('data-count', '0');
            } else {
              option.disabled = false;
              option.setAttribute(
                'data-count',
                index == 0
                  ? this._countTotalItems()
                  : this._countItems(intPosition, this._fetchText(option)),
              );
            }
          }

          event.emit(x, 'reflow');
          event.emit(x, 'refresh');
          event.emit(x, 'updateLabel');
          break;
      }
    }
  }

  // # Helpers

  /**
   * Toggl items between visible/in-visible based on rendering requirements
   */
  _togglItems() {
    const rhytm = this._paginate(
      this._refs.items.length,
      this._data.currentPage,
      this._data.pageSize.current,
      this._data.maxPages,
    );
    this._refs.items.forEach((x) => x.classList.remove('is-visible'));
    for (let x = rhytm.startIndex; x <= rhytm.endIndex; x++) {
      if (typeof this._refs.items[x] != 'undefined') {
        this._refs.items[x].classList.add('is-visible');
      }
    }

    if (this._data.items.length > 10) {
      this._appendInfo();
      this._appendPagination(rhytm.pages, rhytm.currentPage, rhytm.startPage, rhytm.endPage);
    }
  }

  /**
   * _Paginate
   * Calculate required variables for pagination
   * @param {Integer} totalItems - Number of items in total
   * @param {Integer} currentPage - Currently selected page
   * @param {Integer} pageSize - Visible amount of items
   * @param {Integer} maxPages - Maximum number of pages
   */
  _paginate(totalItems, currentPage, pageSize, maxPages) {
    // calculate total pages
    let totalPages = Math.ceil(totalItems / pageSize);

    // ensure current page isn't out of range
    if (currentPage < 1) {
      currentPage = 1;
    } else if (currentPage > totalPages) {
      currentPage = totalPages;
    }

    let startPage, endPage;
    if (totalPages <= maxPages) {
      // total pages less than max so show all pages
      startPage = 1;
      endPage = totalPages;
    } else {
      // total pages more than max so calculate start and end pages
      let maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
      let maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
      if (currentPage <= maxPagesBeforeCurrentPage) {
        // current page near the start
        startPage = 1;
        endPage = maxPages;
      } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
        // current page near the end
        startPage = totalPages - maxPages + 1;
        endPage = totalPages;
      } else {
        // current page somewhere in the middle
        startPage = currentPage - maxPagesBeforeCurrentPage;
        endPage = currentPage + maxPagesAfterCurrentPage;
      }
    }

    // calculate start and end item indexes
    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    // create an array of pages to ng-repeat in the pager control
    let pages = Array.from(Array(endPage + 1 - startPage).keys()).map((i) => startPage + i);

    return {
      totalItems: totalItems,
      currentPage: currentPage,
      pageSize: pageSize,
      totalPages: totalPages,
      startPage: startPage,
      endPage: endPage,
      startIndex: startIndex,
      endIndex: endIndex,
      pages: pages,
    };
  }

  /**
   * Compare and sort items based on values (Default sorts items ascendingly)
   * // # TODO: Find a way to fetch current dateformat from Sitecore, right now hardcoded to US
   * @param {HTMLElement} a - Sibling
   * @param {HTMLElement} b - Next sibling
   */
  _sort(a, b) {
    let itemIndex =
        this._data.sort.current.field === null
          ? 0
          : Array.from(this._refs.header.children).indexOf(this._data.sort.current.field),
      x = a.children[0].children[itemIndex].dataset.sort
        ? a.children[0].children[itemIndex].dataset.sort
        : this._fetchText(a.children[0].children[itemIndex]),
      y = b.children[0].children[itemIndex].dataset.sort
        ? b.children[0].children[itemIndex].dataset.sort
        : this._fetchText(b.children[0].children[itemIndex]);

    // If descending has been required, change parameters
    if (this._data.sort.current.order === 'desc') {
      x = b.children[0].children[itemIndex].dataset.sort
        ? b.children[0].children[itemIndex].dataset.sort
        : this._fetchText(b.children[0].children[itemIndex]);
      y = a.children[0].children[itemIndex].dataset.sort
        ? a.children[0].children[itemIndex].dataset.sort
        : this._fetchText(a.children[0].children[itemIndex]);
    }

    // Execute sorting strategy
    if (
      moment(x, this._data.dateFormat, true).isValid() &&
      moment(y, this._data.dateFormat, true).isValid()
    ) {
      // Date
      if (new Date(x) > new Date(y)) {
        return 1;
      } else if (new Date(x) < new Date(y)) {
        return -1;
      }
      return 0;
    }

    // String
    return !!window.MSInputMethodContext && !!document.documentMode
      ? x.localeCompare(y)
      : x.localeCompare(y, undefined, this._data.sort.options);
  }

  /**
   * Helper for easily creating DOM-element with properties
   * @param {String} tag - tagname
   * @param {Object} args - properties for tag
   * @param {HTMLElement} target - Append element to a HTMLElement
   * @returns {HTMLElement}
   */
  _createElement(tag, args = {}, target) {
    let elem = document.createElement(tag);

    Object.keys(args).forEach((property) => {
      if (/^(inner|on)\w+$/i.test(property)) {
        elem[property] = args[property];
      } else {
        elem.setAttribute(property, args[property]);
      }
    });

    if (target != null) {
      target.appendChild(elem);
    }

    return elem;
  }

  /**
   * Create a unique GUID for use with dynamic fieldnames eg.
   * @returns {GUID}
   */
  _createGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Get current rendered filters
   * @returns {Array}
   */
  _getFilters() {
    return Array.from(
      this._refs.el.querySelectorAll('.datatable__filters input, .datatable__filters select'),
    );
  }

  /**
   * Parse dataset to ensure data can be read correctly
   * @returns {Object} - Parsed & formatted object
   */
  get _dataset() {
    let response = {};
    for (let key in this._refs.el.dataset) {
      try {
        response[key] = JSON.parse(this._refs.el.dataset[key]);
      } catch (e) {
        response[key] = this._refs.el.dataset[key];
      }
    }

    return response;
  }

  // # Data

  /**
   * _Settings
   * Default settings for component
   * @returns {Object}
   */
  get _settings() {
    return {
      currentPage: 1,
      filters: [],
      sort: {
        options:
          !!window.MSInputMethodContext && !!document.documentMode
            ? {}
            : { sensitivity: 'base', numeric: true },
        current: {
          field: null,
          order: 'asc',
        },
        _default: {
          field: null,
          order: 'asc',
        },
      },
      pageSize: {
        current: 10,
        values: [10, 25, 50],
      },
      info: true,
      pagination: true,
      dateFormat: 'MM-DD-YY',
      maxPages: 5,
      order: {
        desc: 'is-desc',
        asc: 'is-asc',
      },
      translations: {
        info: 'Showing {startIndex} to {endIndex} of {total} results',
        noItems: 'No results were found with current filters, please try again',
        clearFilters: 'Clear all filters',
      },
    };
  }
}
